import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Zap, Clock, Calendar, Tag, ShoppingCart, Edit, Trash2, Play, Pause, TrendingUp, TrendingDown, Package, CreditCard, Flame, Star, XCircle, Copy, RefreshCw, Award } from 'lucide-react';
import { toast } from 'sonner';

// Types de promotions
export const PROMO_TYPES = [
  { value: 'FLASH', label: 'Promo Flash', icon: Zap, color: '#EF4444', description: 'Offre limitée dans le temps' },
  { value: 'SEASONAL', label: 'Saisonnière', icon: Calendar, color: '#F59E0B', description: 'Promotion de saison' },
  { value: 'LOYALTY', label: 'Fidélité', icon: Award, color: '#8B5CF6', description: 'Récompense membres fidèles' },
  { value: 'BUNDLE', label: 'Pack/Bundle', icon: Package, color: '#3B82F6', description: 'Offre groupée' },
  { value: 'FIRST_ORDER', label: '1ère Commande', icon: Star, color: '#10B981', description: 'Nouveau client' }
];

// Cibles des promotions
export const PROMO_TARGETS = [
  { value: 'CREDITS', label: 'Crédits', icon: CreditCard },
  { value: 'SERVICES', label: 'Services', icon: Zap },
  { value: 'PRODUCTS', label: 'Produits', icon: Package },
  { value: 'SUBSCRIPTIONS', label: 'Abonnements', icon: RefreshCw },
  { value: 'CART', label: 'Panier', icon: ShoppingCart }
];

// Countdown Widget Component - Programmable
export const CountdownWidget = ({ endDate, size = 'default', showLabels = true, onExpire }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);

  useEffect(() => {
    if (!endDate) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const end = new Date(endDate);
      const difference = end - now;

      if (difference <= 0) {
        setIsExpired(true);
        if (onExpire) onExpire();
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((difference / 1000 / 60) % 60);
      const seconds = Math.floor((difference / 1000) % 60);

      // Urgent si moins de 24h
      setIsUrgent(days === 0 && hours < 24);

      return { days, hours, minutes, seconds };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate, onExpire]);

  if (!endDate) return null;

  const sizeClasses = {
    small: { container: 'p-2', digit: 'text-lg', label: 'text-xs' },
    default: { container: 'p-3', digit: 'text-2xl', label: 'text-xs' },
    large: { container: 'p-4', digit: 'text-4xl', label: 'text-sm' }
  };

  const classes = sizeClasses[size] || sizeClasses.default;
  const bgColor = isExpired ? 'bg-gray-100' : isUrgent ? 'bg-red-600' : 'bg-gradient-to-r from-orange-500 to-red-500';
  const textColor = isExpired ? 'text-gray-500' : 'text-white';

  const timeUnits = [
    { value: timeLeft.days, label: 'Jours', shortLabel: 'J' },
    { value: timeLeft.hours, label: 'Heures', shortLabel: 'H' },
    { value: timeLeft.minutes, label: 'Minutes', shortLabel: 'M' },
    { value: timeLeft.seconds, label: 'Secondes', shortLabel: 'S' }
  ];

  return (
    <div className={`rounded-xl ${bgColor} ${textColor} ${classes.container} shadow-lg`}>
      {isExpired ? (
        <div className="text-center py-2">
          <XCircle className="w-6 h-6 mx-auto mb-1" />
          <span className="font-bold">Promotion expirée</span>
        </div>
      ) : (
        <>
          {isUrgent && (
            <div className="flex items-center justify-center gap-2 mb-2 animate-pulse">
              <Flame className="w-4 h-4" />
              <span className="text-sm font-bold">DERNIÈRES HEURES !</span>
              <Flame className="w-4 h-4" />
            </div>
          )}
          <div className="grid grid-cols-4 gap-2 text-center">
            {timeUnits.map((unit, idx) => (
              <div key={idx} className={`rounded-lg ${isUrgent ? 'bg-red-700' : 'bg-white/20'} ${classes.container}`}>
                <div className={`${classes.digit} font-bold font-mono`}>
                  {String(unit.value).padStart(2, '0')}
                </div>
                {showLabels && (
                  <div className={`${classes.label} opacity-80`}>
                    {size === 'small' ? unit.shortLabel : unit.label}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Promotion Card Component
export const PromoCard = ({ promo, onEdit, onDelete, onToggle, onDuplicate }) => {
  const typeConfig = PROMO_TYPES.find(t => t.value === promo.type) || PROMO_TYPES[0];
  const targetConfig = PROMO_TARGETS.find(t => t.value === promo.cible) || PROMO_TARGETS[0];
  const TypeIcon = typeConfig.icon;
  const TargetIcon = targetConfig.icon;

  const isActive = promo.actif && new Date(promo.date_fin) > new Date();
  const isPending = promo.actif && new Date(promo.date_debut) > new Date();

  return (
    <Card className={`relative overflow-hidden transition-all ${isActive ? 'ring-2 ring-green-500' : isPending ? 'ring-2 ring-blue-500' : 'opacity-75'}`}>
      {/* Status Badge */}
      <div className="absolute top-3 right-3 z-10">
        {isActive && !isPending && (
          <Badge className="bg-green-600 text-white animate-pulse font-semibold">
            <Play className="w-3 h-3 mr-1" /> EN COURS
          </Badge>
        )}
        {isPending && (
          <Badge className="bg-blue-600 text-white font-semibold">
            <Clock className="w-3 h-3 mr-1" /> À VENIR
          </Badge>
        )}
        {!promo.actif && (
          <Badge className="bg-gray-600 text-white font-semibold">
            <Pause className="w-3 h-3 mr-1" /> INACTIVE
          </Badge>
        )}
      </div>

      <CardHeader style={{ backgroundColor: `${typeConfig.color}15` }}>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl" style={{ backgroundColor: typeConfig.color }}>
            <TypeIcon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 pr-20">
            <CardTitle className="text-lg">{promo.nom}</CardTitle>
            <CardDescription className="flex items-center gap-2 mt-1">
              <TargetIcon className="w-4 h-4" />
              {targetConfig.label}
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-4 space-y-4">
        {/* Reduction Display */}
        <div className="text-center p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl">
          <div className="text-4xl font-bold" style={{ color: typeConfig.color }}>
            {promo.type_reduction === 'PERCENTAGE' ? (
              <>{promo.valeur_reduction}%</>
            ) : (
              <>{promo.valeur_reduction}€</>
            )}
          </div>
          <div className="text-sm text-gray-600">
            {promo.type_reduction === 'PERCENTAGE' ? 'de réduction' : 'de remise'}
          </div>
          {promo.code_promo && (
            <div className="mt-2 inline-flex items-center gap-2 bg-white px-3 py-1 rounded-full border-2 border-dashed border-gray-300">
              <Tag className="w-4 h-4 text-gray-500" />
              <span className="font-mono font-bold text-gray-800">{promo.code_promo}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => {
                  navigator.clipboard.writeText(promo.code_promo);
                  toast.success('Code copié !');
                }}
              >
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          )}
        </div>

        {/* Countdown */}
        {isActive && !isPending && (
          <CountdownWidget
            endDate={promo.date_fin}
            size="small"
            onExpire={() => toast.info(`Promotion "${promo.nom}" terminée`)}
          />
        )}

        {/* Description */}
        {promo.description && (
          <p className="text-sm text-gray-600">{promo.description}</p>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 text-center text-sm">
          <div className="bg-gray-50 rounded-lg p-2">
            <div className="font-bold text-gray-800">{promo.utilisations || 0}</div>
            <div className="text-xs text-gray-500">Utilisations</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-2">
            <div className="font-bold text-gray-800">{promo.limite_utilisations || '∞'}</div>
            <div className="text-xs text-gray-500">Limite</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-2">
            <div className="font-bold text-gray-800">{promo.montant_economise || 0}€</div>
            <div className="text-xs text-gray-500">Économies</div>
          </div>
        </div>

        {/* Dates */}
        <div className="flex justify-between text-xs text-gray-500 border-t pt-3">
          <span>Du: {new Date(promo.date_debut).toLocaleDateString('fr-FR')}</span>
          <span>Au: {new Date(promo.date_fin).toLocaleDateString('fr-FR')}</span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={() => onEdit(promo)}>
            <Edit className="w-4 h-4 mr-1" /> Modifier
          </Button>
          <Button variant="outline" size="sm" onClick={() => onDuplicate(promo)}>
            <Copy className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={promo.actif ? 'text-orange-600' : 'text-green-600'}
            onClick={() => onToggle(promo)}
          >
            {promo.actif ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-red-600"
            onClick={() => onDelete(promo)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Stats Card Component
export const StatsCard = ({ icon: Icon, title, value, subtitle, color, trend, trendValue }) => (
  <Card className="border-l-4" style={{ borderLeftColor: color }}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold" style={{ color }}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
        <div className="flex flex-col items-end">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
          {trend && (
            <div className={`flex items-center mt-2 text-xs ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);
